import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, Upload, Loader2, Film } from "lucide-react";
import { toast } from "sonner";
import { Progress } from "@/components/ui/progress";
import { api, errMsg, FILES_BASE } from "@/lib/api";
import { useAm } from "@/components/am/shared";

const CHUNK = 4 * 1024 * 1024;

const videoMeta = (file) => new Promise((res, rej) => {
  const v = document.createElement("video"); v.preload = "metadata";
  v.onloadedmetadata = () => { res({ width: v.videoWidth, height: v.videoHeight, duration: v.duration }); URL.revokeObjectURL(v.src); };
  v.onerror = () => rej(new Error("Could not read this video"));
  v.src = URL.createObjectURL(file);
});

export function Thumb({ m, className = "h-20 w-20" }) {
  if (!m) return <div className={`${className} rounded bg-slate-900 grid place-items-center text-[10px] text-slate-500`}>None</div>;
  return m.media_type === "video" ? <video src={`${FILES_BASE}${m.file_url}`} className={`${className} object-cover rounded`} muted />
    : <img src={`${FILES_BASE}${m.file_url}`} alt={m.title} className={`${className} object-cover rounded`} />;
}

export default function MediaPicker({ type = "image", value, onPick, testid }) {
  const { mediaList, reloadMedia, tree } = useAm();
  const [pct, setPct] = useState(null);
  const inp = useRef(null);
  const list = mediaList.filter((m) => m.media_type === type);
  const upload = async (e) => {
    const f = e.target.files?.[0]; e.target.value = ""; if (!f) return;
    try {
      const meta = type === "video" ? await videoMeta(f) : {};
      const id = crypto.randomUUID().replace(/-/g, ""); const total = Math.max(1, Math.ceil(f.size / CHUNK)); let r;
      setPct(0);
      for (let i = 0; i < total; i++) {
        const fd = new FormData();
        fd.append("file", f.slice(i * CHUNK, (i + 1) * CHUNK)); fd.append("upload_id", id); fd.append("index", i); fd.append("total", total);
        fd.append("filename", f.name); fd.append("content_type", f.type); fd.append("project_id", tree.campaign.project_id || "");
        Object.entries(meta).forEach(([k, v]) => fd.append(k, type === "video" && k === "duration" ? v : Math.round(v)));
        r = await api.post("/ads-manager/media/upload", fd);
        setPct(Math.round(((i + 1) / total) * 100));
      }
      toast.success(r.data.reused ? "Same file already in your library — reused it" : "Uploaded");
      await reloadMedia(); onPick(r.data.id);
    } catch (er) { toast.error(errMsg(er) || er.message); } finally { setPct(null); }
  };
  return (
    <div className="space-y-2" data-testid={testid}>
      <div className="flex gap-2 overflow-x-auto pb-1">
        {list.map((m) => (
          <button key={m.id} onClick={() => onPick(m.id)} title={`${m.title} · ${m.width || "?"}×${m.height || "?"}`} data-testid={`${testid}-item-${m.id}`}
            className={`shrink-0 rounded-lg border-2 ${value === m.id ? "border-emerald-500" : "border-transparent"}`}><Thumb m={m} /></button>))}
        {!list.length && <span className="text-xs text-slate-500">No {type}s in your library yet.</span>}
      </div>
      <div className="flex flex-wrap gap-2 items-center">
        <input ref={inp} type="file" className="hidden" accept={type === "video" ? "video/mp4,video/quicktime" : "image/png,image/jpeg,image/webp"} onChange={upload} data-testid={`${testid}-upload-input`} />
        <button onClick={() => inp.current?.click()} disabled={pct !== null} className="inline-flex items-center gap-1 px-3 py-1.5 text-xs rounded-md border border-slate-800 hover:bg-slate-900" data-testid={`${testid}-upload`}>
          {pct !== null ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : type === "video" ? <Film className="h-3.5 w-3.5" /> : <Upload className="h-3.5 w-3.5" />}Upload {type}</button>
        {type === "image" && <Link to="/creative-studio" className="inline-flex items-center gap-1 px-3 py-1.5 text-xs rounded-md border border-slate-800 hover:bg-slate-900" data-testid={`${testid}-ai`}><Sparkles className="h-3.5 w-3.5" />Create with AI in Creative Studio</Link>}
        {pct !== null && <Progress value={pct} className="w-32 h-2" />}
      </div>
      <p className="text-[10px] text-slate-500">{type === "video" ? "MP4/MOV up to 200 MB, 1 s – 241 min. AI video generation is not available." : "PNG/JPG/WebP up to 30 MB, at least 600 px wide, ratio between 1.91:1 and 9:16."} Library items are reused without uploading again.</p>
    </div>
  );
}
